var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { getSkillsCount } from '../../utils';
import { ARROW_BUTTON_WIDTH, BARS_GAP, BAR_CHART_WIDTH, BAR_HEIGHT, BAR_PARTS_ORDER, BAR_PART_MIN_HEIGHT, BAR_WIDTH, MAX_GROUPS_PER_PAGE, SINGLE_BAR_GROUP_OFFSET, VISIBLE_BARS_COUNT, Y_AXIS_LABELS_WIDTH, } from './constants';
var isGradeMastered = function (grade) {
    var _a = grade.data, notStarted = _a.no_data, developing = _a.developing, helpNeeded = _a.help_needed, mastered = _a.mastered;
    return (notStarted.length === 0 &&
        developing.length === 0 &&
        helpNeeded.length === 0 &&
        mastered.length > 0);
};
var SHORT_NAMES_DIVIDER = '-';
var getMergedGradesShortName = function (grades) {
    var firstGrade = grades[0];
    var lastGrade = grades[grades.length - 1];
    return "" + firstGrade.shortName + SHORT_NAMES_DIVIDER + lastGrade.shortName;
};
var getGradeShortNamesFromMergedName = function (mergedName) {
    return mergedName.split(SHORT_NAMES_DIVIDER);
};
var mergeStudentsSummaryData = function (data) {
    return data.reduce(function (el, student, idx, original) {
        if (el.find(function (st) { return st.id === student.id; }))
            return el;
        var curStudentData = __assign(__assign({}, student), { skillsCount: original
                .filter(function (it) { return it.id === student.id; })
                .reduce(function (count, st) {
                count += st.skillsCount;
                return count;
            }, 0) });
        return __spreadArray(__spreadArray([], el, true), [curStudentData], false);
    }, []);
};
var mergeGradesSkillData = function (grades) {
    return grades.reduce(function (acc, current) {
        return BAR_PARTS_ORDER.reduce(function (obj, status) {
            var _a;
            var _b;
            return __assign(__assign({}, obj), (_a = {}, _a[status] = mergeStudentsSummaryData(__spreadArray(__spreadArray([], ((_b = acc[status]) !== null && _b !== void 0 ? _b : []), true), current.data[status], true)), _a));
        }, {});
    }, {});
};
var getOrderedGradeGroups = function (targetGrades, originalGrades) {
    var result = [];
    var orderedGroup = [];
    targetGrades.forEach(function (grade, idx) {
        if (idx === 0) {
            orderedGroup.push(grade);
            return;
        }
        var prevGrade = orderedGroup[orderedGroup.length - 1];
        if (prevGrade) {
            var originalIdx = originalGrades.findIndex(function (el) { return el.id === grade.id; });
            var prevGradeOriginalIdx = originalGrades.findIndex(function (el) { return el.id === prevGrade.id; });
            if (originalIdx !== prevGradeOriginalIdx + 1) {
                result.push(__spreadArray([], orderedGroup, true));
                orderedGroup.length = 0;
            }
        }
        orderedGroup.push(grade);
    });
    result.push(orderedGroup);
    return result;
};
var mapSummaryToBarData = function (summary) {
    var uniqueGrades = [];
    var barsGroupData = summary.reduce(function (acc, current) {
        if (current.grades.length === 0)
            return acc;
        var data = {
            domainName: current.name,
        };
        var masteredGrades = current.grades.filter(function (el) { return isGradeMastered(el); });
        var orderedMasteredGradesGroups = getOrderedGradeGroups(masteredGrades, current.grades);
        var isMasteredGradesOrdered = orderedMasteredGradesGroups.some(function (group) { return group.length > 1; });
        var handledGrades = [];
        if (isMasteredGradesOrdered) {
            handledGrades = current.grades.filter(function (el) { return !isGradeMastered(el); });
            orderedMasteredGradesGroups.forEach(function (gradesGroup) {
                var targetGrade;
                if (gradesGroup.length > 1) {
                    targetGrade = __assign(__assign({}, gradesGroup[0]), { value: gradesGroup[0].value, shortName: getMergedGradesShortName(gradesGroup), data: mergeGradesSkillData(gradesGroup), totalSkills: gradesGroup.reduce(function (total, grade) {
                            total += grade.totalSkills;
                            return total;
                        }, 0) });
                }
                else {
                    targetGrade = gradesGroup[0];
                }
                var targetGradeIdx = current.grades.findIndex(function (el) { return el.id === gradesGroup[0].id; });
                handledGrades.splice(targetGradeIdx, 0, targetGrade);
            });
        }
        else {
            handledGrades = __spreadArray([], current.grades, true);
        }
        if (handledGrades.length > 0) {
            handledGrades.forEach(function (grade) {
                data[grade.shortName] = {
                    data: grade.data,
                    total: grade.totalSkills,
                };
                if (!uniqueGrades.some(function (el) { return el.shortName === grade.shortName; })) {
                    uniqueGrades.push(grade);
                }
            });
        }
        return __spreadArray(__spreadArray([], acc, true), [data], false);
    }, []);
    var keys = uniqueGrades
        .sort(function (a, b) { return a.value - b.value; })
        .map(function (el) { return el.shortName; });
    return {
        barsGroupData: barsGroupData,
        keys: keys,
    };
};
var getGradeKeysFromBarData = function (data) {
    return Object.keys(data).filter(function (el) { return el !== 'domainName'; });
};
var calculateBarDataPerPage = function (data) {
    var barsPerPage = [];
    var renderedBars = [];
    var barsCounter = 0;
    data.forEach(function (barsGroupData) {
        var gradesNum = getGradeKeysFromBarData(barsGroupData).length;
        barsCounter += gradesNum;
        if (barsCounter > VISIBLE_BARS_COUNT ||
            renderedBars.length === MAX_GROUPS_PER_PAGE) {
            barsPerPage.push(__spreadArray([], renderedBars, true));
            renderedBars.length = 0;
            barsCounter = gradesNum;
            renderedBars.push(barsGroupData);
        }
        else {
            renderedBars.push(barsGroupData);
        }
    });
    if (renderedBars.length > 0)
        barsPerPage.push(renderedBars);
    return barsPerPage;
};
var getEdgeOffset = function (withButtons) {
    return withButtons ? ARROW_BUTTON_WIDTH * 2 : 0;
};
var calculateGroupWidth = function (data) {
    var currentGroupKeys = getGradeKeysFromBarData(data);
    var barsInCurrentGroup = currentGroupKeys.length;
    var groupWidth = BAR_WIDTH * barsInCurrentGroup + BARS_GAP * (barsInCurrentGroup - 1);
    return groupWidth;
};
var calculateGroupsGap = function (_a) {
    var pageData = _a.pageData, withButtons = _a.withButtons;
    if (pageData.length < 1)
        return 0;
    var canvasWidth = BAR_CHART_WIDTH - Y_AXIS_LABELS_WIDTH;
    var edgeOffset = getEdgeOffset(withButtons);
    var workspaceWidth = canvasWidth - edgeOffset;
    var groupsWidth = pageData.reduce(function (acc, current) {
        acc += calculateGroupWidth(current);
        return acc;
    }, 0);
    var space = workspaceWidth - groupsWidth;
    // Edge padding === groups gap, this is the reason we divide space on domains count plus 2 edges
    return space / (pageData.length + 1);
};
var calculateGroupCoords = function (_a) {
    var barsGroupData = _a.barsGroupData, pageData = _a.pageData, groupsGap = _a.groupsGap, withButtons = _a.withButtons;
    var edgeOffset = getEdgeOffset(withButtons);
    var x = edgeOffset;
    if (pageData.length === 1) {
        x = edgeOffset / 2 + SINGLE_BAR_GROUP_OFFSET;
    }
    else {
        var currentGroupIdx = pageData.findIndex(function (el) { return el.domainName === barsGroupData.domainName; });
        var prevGroupsWidth = pageData
            .slice(0, currentGroupIdx)
            .reduce(function (acc, current) {
            acc += calculateGroupWidth(current);
            return acc;
        }, 0);
        x = edgeOffset / 2 + prevGroupsWidth + groupsGap * (currentGroupIdx + 1);
    }
    var groupWidth = calculateGroupWidth(barsGroupData);
    var middleX = x + groupWidth / 2 - BAR_WIDTH / 2;
    return {
        x: x,
        middleX: middleX,
        width: groupWidth,
    };
};
var calculateBarX = function (_a) {
    var data = _a.data, pageData = _a.pageData, groupsGap = _a.groupsGap, withButtons = _a.withButtons;
    var barsGroupData = data.data;
    var currentGroupKeys = getGradeKeysFromBarData(barsGroupData);
    var barsInCurrentGroup = currentGroupKeys.length;
    var currentBarIdx = currentGroupKeys.indexOf(data.id);
    var groupMiddleX = calculateGroupCoords({
        barsGroupData: barsGroupData,
        pageData: pageData,
        groupsGap: groupsGap,
        withButtons: withButtons,
    }).middleX;
    var barMiddleRatio = barsInCurrentGroup % 2
        ? currentBarIdx + 1 - Math.ceil(barsInCurrentGroup / 2)
        : currentBarIdx - barsInCurrentGroup / 2 + 0.5;
    var x = groupMiddleX + (BAR_WIDTH + BARS_GAP) * barMiddleRatio;
    return x;
};
var calculateBarPartsData = function (data) {
    var _a = data.value, skillsData = _a.data, total = _a.total;
    var heightScalePercentage = BAR_HEIGHT / total;
    var y = 0;
    var barPartsData = BAR_PARTS_ORDER.reduce(function (acc, current) {
        var _a;
        var value = getSkillsCount(skillsData[current]);
        var heightInPX = Math.round(value * heightScalePercentage);
        var isHeightExceeds = value > 0 && heightInPX < BAR_PART_MIN_HEIGHT;
        var realHeight = isHeightExceeds ? BAR_PART_MIN_HEIGHT : heightInPX;
        var partData = {
            y: y,
            height: realHeight,
        };
        y += realHeight;
        return __assign(__assign({}, acc), (_a = {}, _a[current] = partData, _a));
    }, {});
    if (y !== BAR_HEIGHT) {
        var heightExcess_1 = y - BAR_HEIGHT;
        var biggestPartStatus_1 = Object.entries(skillsData).sort(function (_a, _b) {
            var _status1 = _a[0], data1 = _a[1];
            var _status2 = _b[0], data2 = _b[1];
            return getSkillsCount(data2) - getSkillsCount(data1);
        })[0][0];
        var biggestPartStatusIdx_1 = Infinity;
        BAR_PARTS_ORDER.forEach(function (status, idx) {
            if (status === biggestPartStatus_1) {
                barPartsData[status] = __assign(__assign({}, barPartsData[status]), { height: barPartsData[status].height - heightExcess_1 });
                biggestPartStatusIdx_1 = idx;
            }
            if (idx > biggestPartStatusIdx_1) {
                barPartsData[status] = __assign(__assign({}, barPartsData[status]), { y: barPartsData[status].y - heightExcess_1 });
            }
        });
    }
    return barPartsData;
};
export { mapSummaryToBarData, calculateBarDataPerPage, calculateBarPartsData, calculateGroupCoords, calculateBarX, calculateGroupsGap, getGradeShortNamesFromMergedName, };
